import React, { useState, useEffect } from 'react';
import MainPage from '../pages/MainPage/MainPage';
import SolutionPage from '../pages/Solutions/SolutionPage';
import PrivacyPolicy from '../pages/Legal/PrivacyPolicy';
import TermsOfUse from '../pages/Legal/TermsOfUse';
import CookiePolicy from '../pages/Legal/CookiePolicy';
import LegalNotice from '../pages/Legal/LegalNotice';
import AboutPage from '../pages/About/AboutPage';
import OverviewPage from '../pages/About/OverviewPage';
import BoardPage from '../pages/About/BoardPage';
import StrategyPage from '../pages/About/StrategyPage';
import GovernancePage from '../pages/About/GovernancePage';
import ContactPage from '../pages/Contact/ContactPage';
import PressPage from '../pages/Press/PressPage';
import CareersPage from '../pages/Careers/CareersPage';
import BrandPage from '../pages/Brand/BrandPage';
import { useTranslation } from '../hooks/useTranslation';

interface RouterProps { }

const Router: React.FC<RouterProps> = () => {
  const [currentPath, setCurrentPath] = useState(window.location.pathname);
  const { translations: t } = useTranslation();

  // Scroll to top when path changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [currentPath]);

  useEffect(() => {
    const handleLocationChange = () => {
      setCurrentPath(window.location.pathname);
    };

    // Listen for popstate events (back/forward button)
    window.addEventListener('popstate', handleLocationChange);

    // Override the default link behavior
    const handleLinkClick = (e: Event) => {
      const target = e.target as HTMLAnchorElement;
      if (target && target.tagName === 'A' && target.href.includes(window.location.origin)) {
        e.preventDefault();
        const newPath = new URL(target.href).pathname;
        window.history.pushState({}, '', newPath);
        handleLocationChange();
      }
    };

    document.addEventListener('click', handleLinkClick);

    return () => {
      window.removeEventListener('popstate', handleLocationChange);
      document.removeEventListener('click', handleLinkClick);
    };
  }, []);

  // Simple routing logic
  const renderPage = () => {
    // Remove leading slash for easier matching
    const path = currentPath.replace(/^\//, '');

    // Home page - página principal
    if (path === '' || path === 'index.html' || path === 'main' || path === 'home') {
      return <MainPage />;
    }


    // Solution pages
    if (path.startsWith('solution/')) {
      const solutionId = path.replace('solution/', '');
      return <SolutionPage solutionId={solutionId} />;
    }

    // About pages
    if (path === 'about') {
      return <AboutPage />;
    }
    if (path === 'about/overview') {
      return <OverviewPage />;
    }
    if (path === 'about/board') {
      return <BoardPage />;
    }
    if (path === 'about/strategy') {
      return <StrategyPage />;
    }
    if (path === 'about/governance') {
      return <GovernancePage />;
    }

    // Contact page
    if (path === 'contact') {
      return <ContactPage />;
    }

    // Press/Imprensa page
    if (path === 'imprensa' || path === 'press') {
      return <PressPage />;
    }

    // Careers page
    if (path === 'careers') {
      return <CareersPage />;
    }

    // Brand guidelines page
    if (path === 'brand' || path === 'brand-guidelines' || path === 'media-kit') {
      return <BrandPage />;
    }

    // Redirections for missing routes
    if (path === 'news') {
      // Redirect to press page
      window.history.replaceState({}, '', '/press');
      setCurrentPath('/press');
      return <PressPage />;
    }

    if (path === 'documentation') {
      // Redirect to documentacao
      window.history.replaceState({}, '', '/documentacao');
      setCurrentPath('/documentacao');
      return (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          flexDirection: 'column',
          background: '#f8f9fa',
          textAlign: 'center',
          padding: '2rem'
        }}>
          <h1 style={{ color: '#2c3e50', marginBottom: '1rem' }}>
            {t.router.documentation}
          </h1>
          <p style={{ color: '#666', marginBottom: '2rem' }}>
            {t.router.comingSoon}
          </p>
          <button
            onClick={() => {
              window.history.pushState({}, '', '/');
              setCurrentPath('/');
            }}
            style={{
              background: '#3498db',
              color: 'white',
              border: 'none',
              padding: '1rem 2rem',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: '500'
            }}
          >
            {t.router.backToHome}
          </button>
        </div>
      );
    }

    if (path === 'support') {
      // Redirect to suporte
      window.history.replaceState({}, '', '/suporte');
      setCurrentPath('/suporte');
      return (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          flexDirection: 'column',
          background: '#f8f9fa',
          textAlign: 'center',
          padding: '2rem'
        }}>
          <h1 style={{ color: '#2c3e50', marginBottom: '1rem' }}>
            {t.router.support}
          </h1>
          <p style={{ color: '#666', marginBottom: '2rem' }}>
            {t.router.comingSoon}
          </p>
          <button
            onClick={() => {
              window.history.pushState({}, '', '/');
              setCurrentPath('/');
            }}
            style={{
              background: '#3498db',
              color: 'white',
              border: 'none',
              padding: '1rem 2rem',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: '500'
            }}
          >
            {t.router.backToHome}
          </button>
        </div>
      );
    }

    if (path === 'partnerships') {
      // Redirect to parcerias
      window.history.replaceState({}, '', '/parcerias');
      setCurrentPath('/parcerias');
      return (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          flexDirection: 'column',
          background: '#f8f9fa',
          textAlign: 'center',
          padding: '2rem'
        }}>
          <h1 style={{ color: '#2c3e50', marginBottom: '1rem' }}>
            {t.router.partnerships}
          </h1>
          <p style={{ color: '#666', marginBottom: '2rem' }}>
            {t.router.comingSoon}
          </p>
          <button
            onClick={() => {
              window.history.pushState({}, '', '/');
              setCurrentPath('/');
            }}
            style={{
              background: '#3498db',
              color: 'white',
              border: 'none',
              padding: '1rem 2rem',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: '500'
            }}
          >
            {t.router.backToHome}
          </button>
        </div>
      );
    }

    if (path === 'insights') {
      // For now, redirect to contact for more information
      return (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          flexDirection: 'column',
          background: '#f8f9fa',
          textAlign: 'center',
          padding: '2rem'
        }}>
          <h1 style={{ color: '#2c3e50', marginBottom: '1rem' }}>
            {t.router.insights}
          </h1>
          <p style={{ color: '#666', marginBottom: '2rem' }}>
            {t.router.comingSoon}
          </p>
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', justifyContent: 'center' }}>
            <button
              onClick={() => {
                window.history.pushState({}, '', '/contact');
                setCurrentPath('/contact');
              }}
              style={{
                background: '#2c5530',
                color: 'white',
                border: 'none',
                padding: '1rem 2rem',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: '500'
              }}
            >
              {t.router.contactUs}
            </button>
            <button
              onClick={() => {
                window.history.pushState({}, '', '/');
                setCurrentPath('/');
              }}
              style={{
                background: '#3498db',
                color: 'white',
                border: 'none',
                padding: '1rem 2rem',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: '500'
              }}
            >
              {t.router.backToHome}
            </button>
          </div>
        </div>
      );
    }

    if (path === 'privacy-policy') {
      return <PrivacyPolicy />;
    }
    if (path === 'terms-of-service') {
      return <TermsOfUse />;
    }
    if (path === 'cookie-policy') {
      return <CookiePolicy />;
    }
    if (path === 'legal-notice') {
      return <LegalNotice />;
    }


    // Generic pages (placeholders)
    if (['consultoria', 'suporte', 'treinamentos', 'documentacao', 'api', 'parcerias', 'blog', 'sobre-nos'].includes(path)) {
      return (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          flexDirection: 'column',
          background: '#f8f9fa',
          textAlign: 'center',
          padding: '2rem'
        }}>
          <h1 style={{ color: '#2c3e50', marginBottom: '1rem' }}>
            {getPageTitle(path)}
          </h1>
          <p style={{ color: '#666', marginBottom: '2rem' }}>
            Esta seção estará disponível em breve.
          </p>
          <button
            onClick={() => {
              window.history.pushState({}, '', '/');
              setCurrentPath('/');
            }}
            style={{
              background: '#3498db',
              color: 'white',
              border: 'none',
              padding: '1rem 2rem',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: '500'
            }}
          >
            Voltar ao Início
          </button>
        </div>
      );
    }

    // 404 page
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        flexDirection: 'column',
        background: '#f8f9fa',
        textAlign: 'center',
        padding: '2rem'
      }}>
        <h1 style={{ color: '#e74c3c', marginBottom: '1rem' }}>404</h1>
        <h2 style={{ color: '#2c3e50', marginBottom: '1rem' }}>Página não encontrada</h2>
        <p style={{ color: '#666', marginBottom: '2rem' }}>
          A página que você está procurando não existe ou foi movida.
        </p>
        <button
          onClick={() => {
            window.history.pushState({}, '', '/');
            setCurrentPath('/');
          }}
          style={{
            background: '#3498db',
            color: 'white',
            border: 'none',
            padding: '1rem 2rem',
            borderRadius: '6px',
            cursor: 'pointer',
            fontWeight: '500'
          }}
        >
          Voltar ao Início
        </button>
      </div>
    );
  };

  const getPageTitle = (path: string) => {
    const titles: { [key: string]: string } = {
      'consultoria': 'Consultoria Geral',
      'suporte': 'Suporte Técnico',
      'treinamentos': 'Treinamentos',
      'documentacao': 'Documentação',
      'api': 'API & Integração',
      'parcerias': 'Parcerias',
      'blog': 'Blog',
      'about': 'Sobre Nós'
    };
    return titles[path] || 'Página em Desenvolvimento';
  };

  return (
    <div className="app-router">
      {renderPage()}
    </div>
  );
};

export default Router;
